import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { ApiError } from "../lib/api.ts";
import { authStateQuery, type SessionResponse, sessionQuery } from "../lib/auth.ts";

export async function currentSession(queryClient: QueryClient): Promise<SessionResponse | null> {
  try {
    return await queryClient.fetchQuery(sessionQuery);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export async function requireSession(
  queryClient: QueryClient,
  href: string,
): Promise<SessionResponse> {
  const state = await queryClient.ensureQueryData(authStateQuery);
  // The public demo never needs its admin: visitors come in from the setup page too.
  if (state.needsSetup && !state.demo) throw redirect({ to: "/setup" });
  const session = await currentSession(queryClient);
  if (!session) {
    throw state.needsSetup
      ? redirect({ to: "/setup" })
      : redirect({ to: "/login", search: { redirect: href } });
  }
  return session;
}

export async function guardSetup(queryClient: QueryClient): Promise<void> {
  const state = await queryClient.fetchQuery(authStateQuery);
  if (!state.needsSetup) throw redirect({ to: "/login" });
}

export async function guardLogin(queryClient: QueryClient): Promise<void> {
  const state = await queryClient.ensureQueryData(authStateQuery);
  if (state.needsSetup) throw redirect({ to: "/setup" });
  if (await currentSession(queryClient)) throw redirect({ to: "/" });
}

/** Only same-site absolute paths: blocks open redirects like //evil.example or /\evil.example. */
export function safeRedirect(target: string | undefined): string {
  if (!target?.startsWith("/") || target.startsWith("//") || target.startsWith("/\\")) return "/";
  return target;
}

type SessionRouter = {
  state: { location: { href: string } };
  navigate: (opts: { to: "/login"; search: { redirect: string } }) => Promise<void>;
};

/** The hub ended the session while the app was open: sign in again, then come back here. */
export function expireSession(queryClient: QueryClient, router: SessionRouter): void {
  // Drop the cached session first, or /login's guard would see it and bounce straight back.
  queryClient.removeQueries({ queryKey: sessionQuery.queryKey });
  void router.navigate({ to: "/login", search: { redirect: router.state.location.href } });
}
